import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import * as _ from 'lodash-es';
import { F1120SchM2RetainedEarningsService } from './f1120-sch-m2-retained-earnings.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120-sch-m2-retained-earnings-ce',
    templateUrl: './f1120-sch-m2-retained-earnings.component.html',
    styleUrls: ['./f1120-sch-m2-retained-earnings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120SchM2RetainedEarningsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  modalTitle = "Edit Retained Earnings";
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  showFormLoader = true;
  crudLoading = false;
  entityList: any[] = [];

  // Component properties migrated from F1120RetainEarningsCtrl
  userSettings: any;
  logged_in_user: string = '';
  currentyear: string = '';
  scenario: string = '';
  globalScenarioDesc: string = '';
  glbl_issue_short_desc: string = '';
  glbl_issue_id: string = '';
  local_issue_id: string = '';
  isValidScenario = false;
  scheduleM2List: any[] = [];
  scheduleM2Through4List: any[] = [];
  scheduleM2Through8List: any[] = [];
  scheduleM2OtherList: any[] = [];
  scheduleM2HiddenList: any[] = [];
  tabName = "ScheduleM2";
  showDiagLoader = true;
  batchLoader = true;
  showBatchLoader = true;
  loading = true;
  saving = false;
  showBatchData = false;
  clickedDiagTab = false;
  noRecords = false;
  scheduleM2Header: any = {};
  formListOriginal: any[] = [];
  activeTab: number = 0;
  desc: string = '';
  line_no: string = '';
  value: string = '';
  toggleTotal = false;
  systemAccts: any[] = [];
  localAccts: any[] = [];
  batchList: any[] = [];
  batchItem: any;
  batchItemData: any[] = [];
  retainEarningsObj: any = {
    otherIncreases: [],
    otherDecreases: []
  };
  checked704: any;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120SchM2Service: F1120SchM2RetainedEarningsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {}
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!(this.rowData && Object.keys(this.rowData).length > 0)) {
        this.showFormLoader = true;
        this.cdr.markForCheck();
        return;
      }
      this.setupEntityData();
      this.showFormLoader = false;
      if (!(this.filterParams && Object.keys(this.filterParams).length > 0)) {
        this.cdr.markForCheck();
        return;
      }
      this.setupInitialData();
      this.fetchSchM2records();
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
    }
  }

  private setupEntityData() {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    // Entity data will be set from gridData in AngularJS, but we'll use rowData as fallback
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.entityList = [
        {key: "Tax Year", value: this.rowData.TAX_YEAR || this.filterParams?.tax_year || ''},
        {key: "LEID", value: this.rowData.LEID || ''},
        {key: "LE Name", value: this.rowData.LE_NAME || ''}
      ];
    }
  }

  private setupInitialData() {
    if (this.appService.USER_SETTINGS && this.appService.USER_SETTINGS.user) {
      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings.user.sso_id;
    }
    
    this.currentyear = this.filterParams?.tax_year || this.rowData?.TAX_YEAR || '';
    this.scenario = this.globalService?.globalParams?.scenario || '';
    
    // Setup retainEarningsObj from rowData
    if (this.rowData) {
      this.retainEarningsObj = {
        entityName: this.rowData.LE_NAME || '',
        taxYearBegin: this.rowData.TAX_YEAR_BEGIN ? this.formatDate(this.rowData.TAX_YEAR_BEGIN, 'MM/dd') : '',
        taxYearEnd: this.rowData.TAX_YEAR_END ? this.formatDate(this.rowData.TAX_YEAR_END, 'MM/dd/yyyy') : '',
        meCode: this.rowData.ME_CODE || '',
        meName: this.rowData.ME_NAME || '',
        taxCode: this.rowData.CDR_NO || '',
        otherIncreases: [],
        otherDecreases: []
      };
    }
    
    if (this.globalService?.globalParams) {
      this.glbl_issue_short_desc = this.globalService.globalParams.issue_short_desc || '';
      this.glbl_issue_id = this.globalService.globalParams.issue_id || '';
    }
  }

  formatDate(date: any, format: string): string {
    if (!date) return '';
    const d = new Date(date);
    if (format === 'MM/dd') {
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      return `${month}/${day}`;
    } else if (format === 'MM/dd/yyyy') {
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      const year = d.getFullYear();
      return `${month}/${day}/${year}`;
    }
    return '';
  }

  /**
   * Helper method to construct URLs properly, avoiding double slashes
   * @param baseUrl Base URL (may or may not end with /)
   * @param path Path to append (should start with /)
   * @returns Properly constructed URL
   */
  private buildUrl(baseUrl: string, path: string): string {
    if (!baseUrl) return path;
    // Remove all trailing slashes from baseUrl
    const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
    // Remove leading slashes from path and ensure it starts with /
    const cleanPath = '/' + path.replace(/^\/+/, '');
    // Combine and remove any double slashes that might occur
    const fullUrl = (cleanBaseUrl + cleanPath).replace(/([^:]\/)\/+/g, '$1');
    return fullUrl;
  }

  fetchSchM2records() {
    if (!this.GENERAL_CONFIG?.base_url || !this.logged_in_user) {
      return;
    }

    // Set loading state BEFORE making the API call
    this.loading = true;
    this.showFormLoader = true;
    // Force change detection to show loading indicator immediately
    this.cdr.detectChanges();

    const params = {
      "action_code": 'qm7vc2',
      "sso_id": this.logged_in_user,
      "tax_year": this.currentyear,
      "scenario": this.filterParams?.scenario || this.scenario,
      "jcd_key": this.globalService?.globalParams?.jcd_key || this.rowData?.JCD_KEY || '',
      "combination_key": this.rowData.COMBINATION_KEY,
      "issue_key": this.rowData.ISSUE_KEY
    };

    const requestUrl = this.buildUrl(this.GENERAL_CONFIG.base_url, '/loadJsonObject');
    console.log('Fetching Schedule M2 records from:', requestUrl);
    console.log('Loading state set to:', this.loading, this.showFormLoader);

    this.jsonObjectFactory.getJSONObj(
      requestUrl,
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        // Reset state before processing new data
        this.isValidScenario = false;
        
        this.scheduleM2Header = data.jsonObject[0];
        
        // Get raw list and deduplicate based on unique key combination
        const rawList = data.jsonObject.slice(1);
        
        // Deduplicate: Use combination of keys that should be unique
        const seenKeys = new Set<string>();
        this.scheduleM2List = rawList.filter((item: any) => {
          // Create unique key from combination_key, form_line_key, and line_attrib_key
          const uniqueKey = `${item.COMBINATION_KEY || ''}_${item.FORM_LINE_KEY || ''}_${item.LINE_ATTRIB_KEY || ''}_${item.ACCT_REF_KEY || ''}`;
          if (seenKeys.has(uniqueKey)) {
            return false; // Duplicate, skip it
          }
          seenKeys.add(uniqueKey);
          return true;
        });

        // Clear arrays before populating to avoid duplicates
        this.scheduleM2Through4List = [];
        this.scheduleM2Through8List = [];
        this.scheduleM2OtherList = [];
        this.scheduleM2HiddenList = [];

        // Initialize ATTRIB_VALUE for all items to ensure proper binding
        for (let i = 0; i < this.scheduleM2List.length; i++) {
          // Ensure ATTRIB_VALUE is properly set (convert null/undefined to empty string or 0)
          if (this.scheduleM2List[i].ATTRIB_VALUE === null || this.scheduleM2List[i].ATTRIB_VALUE === undefined) {
            if (this.scheduleM2List[i].DATA_TYPE === 'number') {
              this.scheduleM2List[i].ATTRIB_VALUE = 0;
            } else {
              this.scheduleM2List[i].ATTRIB_VALUE = '';
            }
          } else if (this.scheduleM2List[i].DATA_TYPE === 'number' && typeof this.scheduleM2List[i].ATTRIB_VALUE === 'string') {
            // Convert string numbers to actual numbers for proper binding
            const numValue = parseFloat(this.scheduleM2List[i].ATTRIB_VALUE);
            this.scheduleM2List[i].ATTRIB_VALUE = isNaN(numValue) ? 0 : numValue;
          }
        }

        if (this.scheduleM2Header.FORM_NAME == 'F1120') {
          for (let i = 0; i < this.scheduleM2List.length; i++) {
            if (!this.isValidScenario) {
              this.isValidScenario = this.checkEditScenario(
                this.scheduleM2List[i].T_SCENARIO_TYPE_CODE,
                this.scheduleM2List[i].T_SCENARIO,
                this.scheduleM2List[i].ISSUE_KEY
              );
            }

            if (this.scheduleM2List[i].LINE_NO <= '4') {
              this.scheduleM2Through4List.push(this.scheduleM2List[i]);
            } else if (this.scheduleM2List[i].LINE_NO == 'N/A') {
              this.scheduleM2OtherList.push(this.scheduleM2List[i]);
            } else {
              if (this.scheduleM2List[i].ATTRIB_SHORT_CODE != 'CASH_AUTOCALC') {
                this.scheduleM2Through8List.push(this.scheduleM2List[i]);
              } else {
                this.scheduleM2HiddenList.push(this.scheduleM2List[i]);
              }
            }
          }
        } else {
          for (let i = 0; i < this.scheduleM2List.length; i++) {
            if (!this.isValidScenario) {
              this.isValidScenario = this.checkEditScenario(
                this.scheduleM2List[i].T_SCENARIO_TYPE_CODE,
                this.scheduleM2List[i].T_SCENARIO,
                this.scheduleM2List[i].ISSUE_KEY
              );
            }
            if (this.scheduleM2List[i].LINE_NO <= '5') {
              this.scheduleM2Through4List.push(this.scheduleM2List[i]);
            } else if (this.scheduleM2List[i].LINE_NO == 'N/A') {
              this.scheduleM2OtherList.push(this.scheduleM2List[i]);
            } else {
              if (this.scheduleM2List[i].ATTRIB_SHORT_CODE != 'CASH_AUTOCALC') {
                this.scheduleM2Through8List.push(this.scheduleM2List[i]);
              } else {
                this.scheduleM2HiddenList.push(this.scheduleM2List[i]);
              }
            }
          }
        }

        this.formListOriginal = _.cloneDeep(this.scheduleM2List);
        
        // CRITICAL: Calculate all totals and derived fields (including last two fields)
        // This ensures Adjusted Retained Earnings and Unallocated Variance are calculated on initial load
        // Match AngularJS behavior - calculations should run after data is loaded
        this.updateOtherIncreases();
        
        this.loading = false;
        this.showFormLoader = false;
        // Trigger change detection after data is processed and calculations are complete
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error fetching Schedule M2 records:', error);
        console.error('Error details:', error.message || error);
        this.loading = false;
        this.showFormLoader = false;
        this._snackBar.open('Error loading data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  checkEditScenario(s_type_code: string, t_scenario: string, t_issue: string): boolean {
    if (this.scenario == t_scenario && ((s_type_code == 'EXAM' && this.glbl_issue_id == t_issue) || s_type_code != 'EXAM')) {
      return true;
    }
    return false;
  }

  switchTab(input: number) {
    this.showDiagLoader = true;
    if (input === 0) {
      this.toggleTotal = false;
      this.tabName = "ScheduleM2";
      this.activeTab = 0;
    }
    if (input === 1) {
      this.clickedDiagTab = true;
      this.tabName = "Details";
      this.activeTab = 1;
    } else if (input === 2) {
      this.showBatchData = false;
      this.tabName = "Audit";
      this.activeTab = 2;
      this.getAuditSummary();
    }
    this.cdr.detectChanges();
  }

  getAuditSummary() {
    if (!this.GENERAL_CONFIG?.base_url) {
      return;
    }

    const params = {
      "action_code": "rq79be",
      "tax_year": this.currentyear,
      "scenario": this.filterParams?.scenario || this.scenario,
      "jcd_key": this.globalService?.globalParams?.jcd_key || this.rowData?.JCD_KEY || '',
      "form_name": null,
      "combination_key": this.rowData.COMBINATION_KEY
    };

    this.jsonObjectFactory.getJSONObj(
      this.buildUrl(this.GENERAL_CONFIG.base_url, '/loadJsonObject'),
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.batchList = data.jsonObject;

        if (!Array.isArray(this.batchList) || !this.batchList.length) {
          this.noRecords = true;
        }

        for (let i = 0; i < this.batchList.length; i++) {
          const cr_date = this.batchList[i].CR_DATE;
          const actual_date = cr_date.substring(0, 10);
          this.batchList[i].CR_DATE = actual_date;
          this.batchList[i].TOGGLE = false;
        }

        this.batchList.reverse();
        this.showBatchLoader = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error getting audit summary:', error);
        this.showBatchLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  goToTab2(acct_ref_key: string, desc: string, line_no: string, attrib_value: string) {
    this.clickedDiagTab = false;
    this.activeTab = 1;
    this.desc = desc;
    this.line_no = line_no;
    this.value = attrib_value;
    this.tabName = "Details";

    const params = {
      "action_code": 'ena68k',
      "tax_year": this.currentyear,
      "scenario": this.filterParams?.scenario || this.scenario,
      "jcd_key": this.globalService?.globalParams?.jcd_key || this.rowData?.JCD_KEY || '',
      "acct_ref_key": acct_ref_key,
      "combination_key": this.rowData.COMBINATION_KEY
    };

    this.jsonObjectFactory.getJSONObj(
      this.buildUrl(this.GENERAL_CONFIG.base_url, '/loadJsonObject'),
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const accountData = data.jsonObject;
        const systemAccts: any[] = [];
        const localAccts: any[] = [];
        
        for (let i = 0; i < accountData.length; i++) {
          const line = accountData[i];
          if (line.ACCT_LEVEL === 2) {
            systemAccts.push(accountData[i]);
            line.TOGGLE = false;
          } else if (line.ACCT_LEVEL === 3) {
            localAccts.push(accountData[i]);
          }
        }

        this.systemAccts = systemAccts;
        this.localAccts = localAccts;
        this.showDiagLoader = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error getting account details:', error);
        this.showDiagLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  showUploads(stage_key: string) {
    if (!this.GENERAL_CONFIG?.base_url) {
      return;
    }

    const params = {
      "action_code": "fgiz9j",
      "tax_year": this.currentyear,
      "scenario": this.filterParams?.scenario || this.scenario,
      "jcd_key": this.globalService?.globalParams?.jcd_key || this.rowData?.JCD_KEY || '',
      "form_name": null,
      "trans_key": stage_key
    };

    this.jsonObjectFactory.getJSONObj(
      this.buildUrl(this.GENERAL_CONFIG.base_url, '/loadJsonObject'),
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        let batchItem: any;

        for (let i = 0; i < this.batchList.length; i++) {
          if (this.batchList[i].STAGE_KEY === stage_key) {
            batchItem = this.batchList[i];
          }
          this.batchList[i].TOGGLE = false;
        }

        if (this.showBatchData === true) {
          if (this.batchItem && this.batchItem.STAGE_KEY === batchItem.STAGE_KEY) {
            this.showBatchData = false;
            this.cdr.detectChanges();
            return;
          }
        }

        this.batchItem = batchItem;
        this.batchItemData = data.jsonObject;

        for (let i = 0; i < this.batchItemData.length; i++) {
          const line = this.batchItemData[i];
          const attrib_desc = line.ATTRIB_DESCRIPTION;
          for (let j = 0; j < this.scheduleM2List.length; j++) {
            const scheduleM2Line = this.scheduleM2List[j];
            const list_attrib_desc = scheduleM2Line.ATTRIB_DESCRIPTION;
            const is_editable_cell = scheduleM2Line.IS_EDITABLE_CELL;

            if (attrib_desc === list_attrib_desc) {
              if (is_editable_cell === 'N') {
                line.IS_GREYED_OUT = true;
              } else {
                line.IS_GREYED_OUT = false;
              }
            }
          }
        }

        this.showBatchData = true;
        if (batchItem) {
          batchItem.TOGGLE = true;
        }
        this.batchLoader = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error showing uploads:', error);
        this.batchLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Helper to safely convert a value to an integer (matching AngularJS parseInt behavior)
   */
  private toInt(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const num = parseInt(String(value), 10);
    return isNaN(num) ? 0 : num;
  }

  /**
   * Helper method to sync calculated values back to master scheduleM2List
   * This ensures all calculated totals are reflected in the master list
   */
  private syncCalculatedValuesToMasterList(): void {
    // Sync scheduleM2Through4List values back to master list
    this.scheduleM2Through4List.forEach(item => {
      const masterItem = this.scheduleM2List.find(m => 
        m.LINE_ATTRIB_KEY === item.LINE_ATTRIB_KEY || 
        (m.FORM_LINE_KEY === item.FORM_LINE_KEY && m.LINE_ATTRIB_KEY === item.LINE_ATTRIB_KEY)
      );
      if (masterItem) {
        masterItem.ATTRIB_VALUE = item.ATTRIB_VALUE;
      }
    });

    // Sync scheduleM2Through8List values back to master list
    this.scheduleM2Through8List.forEach(item => {
      const masterItem = this.scheduleM2List.find(m => 
        m.LINE_ATTRIB_KEY === item.LINE_ATTRIB_KEY || 
        (m.FORM_LINE_KEY === item.FORM_LINE_KEY && m.LINE_ATTRIB_KEY === item.LINE_ATTRIB_KEY)
      );
      if (masterItem) {
        masterItem.ATTRIB_VALUE = item.ATTRIB_VALUE;
      }
    });

    // Sync scheduleM2OtherList values back to master list
    this.scheduleM2OtherList.forEach(item => {
      const masterItem = this.scheduleM2List.find(m => 
        m.LINE_ATTRIB_KEY === item.LINE_ATTRIB_KEY || 
        (m.FORM_LINE_KEY === item.FORM_LINE_KEY && m.LINE_ATTRIB_KEY === item.LINE_ATTRIB_KEY)
      );
      if (masterItem) {
        masterItem.ATTRIB_VALUE = item.ATTRIB_VALUE;
      }
    });
  }

  updateOtherIncreases(retainEarnings?: any) {
    // Normalize undefined/NaN values to 0 (matching AngularJS behavior)
    // IMPORTANT: This must run BEFORE calculations to ensure all values are numbers
    _.forEach(this.scheduleM2Through4List, (a: any) => {
      if ((a.ATTRIB_VALUE == undefined || isNaN(a.ATTRIB_VALUE)) && a.IS_EDITABLE_CELL == "Y" && a.DATA_TYPE == "number") {
        a.ATTRIB_VALUE = 0;
      }
      // Ensure numeric values are actually numbers, not strings
      if (a.DATA_TYPE == "number") {
        if (typeof a.ATTRIB_VALUE === 'string') {
          const numValue = parseFloat(a.ATTRIB_VALUE);
          a.ATTRIB_VALUE = isNaN(numValue) ? 0 : numValue;
        } else if (a.ATTRIB_VALUE != null && !isNaN(a.ATTRIB_VALUE)) {
          // Ensure it's a proper number (not string that looks like number)
          a.ATTRIB_VALUE = this.toInt(a.ATTRIB_VALUE);
        }
      }
    });

    _.forEach(this.scheduleM2Through8List, (a: any) => {
      if ((a.ATTRIB_VALUE == undefined || isNaN(a.ATTRIB_VALUE)) && a.IS_EDITABLE_CELL == "Y" && a.DATA_TYPE == "number") {
        a.ATTRIB_VALUE = 0;
      }
      // Ensure numeric values are actually numbers, not strings
      // CRITICAL: This ensures Cash value at index [1] is properly converted
      if (a.DATA_TYPE == "number") {
        if (typeof a.ATTRIB_VALUE === 'string') {
          const numValue = parseFloat(a.ATTRIB_VALUE);
          a.ATTRIB_VALUE = isNaN(numValue) ? 0 : numValue;
        } else if (a.ATTRIB_VALUE != null && !isNaN(a.ATTRIB_VALUE)) {
          // Ensure it's a proper number (not string that looks like number)
          a.ATTRIB_VALUE = this.toInt(a.ATTRIB_VALUE);
        }
      }
    });

    // Match AngularJS: In AngularJS, updateOtherIncreases() is called without params,
    // but uses retainEarnings.FORM_NAME. We'll use scheduleM2Header.FORM_NAME as fallback
    const formName = (retainEarnings && retainEarnings.FORM_NAME) 
      ? retainEarnings.FORM_NAME 
      : (this.scheduleM2Header?.FORM_NAME || 'F1120');

    if (formName == 'F1120') {
      // Total Increases Line 3
      if (this.scheduleM2Through4List != null && this.scheduleM2Through4List.length > 15) {
        // Convert to numbers and calculate (matching AngularJS parseInt behavior)
        this.scheduleM2Through4List[15].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through4List[3]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[4]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[5]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[6]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[7]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[8]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[9]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[10]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[11]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[12]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[13]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[14]?.ATTRIB_VALUE);

        // Total For Subline(Sum only input fields)
        this.scheduleM2Through4List[16].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through4List[3]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[4]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[5]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[6]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[7]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[8]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[11]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[12]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[13]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[14]?.ATTRIB_VALUE);
      }

      // Total Line 4
      if (this.scheduleM2Through4List != null && this.scheduleM2Through4List.length > 17) {
        this.scheduleM2Through4List[17].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through4List[0]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[1]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[15]?.ATTRIB_VALUE);
      }

      // Total Decreases Line 6
      if (this.scheduleM2Through8List != null && this.scheduleM2Through8List.length > 15) {
        this.scheduleM2Through8List[15].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through8List[4]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[5]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[6]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[7]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[8]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[9]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[10]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[11]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[12]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[13]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[14]?.ATTRIB_VALUE);

        // Total For Subline(Sum only input fields)
        this.scheduleM2Through8List[16].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through8List[4]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[5]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[6]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[7]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[8]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[11]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[12]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[13]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[14]?.ATTRIB_VALUE);
      }

      // Total Line 7
      if (this.scheduleM2Through8List != null && this.scheduleM2Through8List.length > 17) {
        this.scheduleM2Through8List[17].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through8List[1]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[2]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[3]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[15]?.ATTRIB_VALUE);
      }

      // Total Line 8
      if (this.scheduleM2Through8List != null && this.scheduleM2Through8List.length > 18 && 
          this.scheduleM2Through4List != null && this.scheduleM2Through4List.length > 17) {
        this.scheduleM2Through8List[18].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through4List[17]?.ATTRIB_VALUE) - 
          this.toInt(this.scheduleM2Through8List[17]?.ATTRIB_VALUE);
      }

      if (this.scheduleM2OtherList != null && this.scheduleM2OtherList.length > 5) {
        this.scheduleM2OtherList[5].ATTRIB_VALUE = this.toInt(this.scheduleM2Through8List[18]?.ATTRIB_VALUE);
        this.scheduleM2OtherList[3].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2OtherList[1]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2OtherList[2]?.ATTRIB_VALUE);
        this.scheduleM2OtherList[6].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2OtherList[3]?.ATTRIB_VALUE) -
          this.toInt(this.scheduleM2OtherList[5]?.ATTRIB_VALUE);
      }
    } else {
      // F1065 logic
      if (this.scheduleM2Through4List != null && this.scheduleM2Through4List.length > 18) {
        this.scheduleM2Through4List[18].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through4List[6]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[7]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[8]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[9]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[10]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[11]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[12]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[13]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[14]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[15]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[16]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[17]?.ATTRIB_VALUE);

        this.scheduleM2Through4List[19].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through4List[6]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[7]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[8]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[9]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[10]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[11]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[14]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[15]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[16]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[17]?.ATTRIB_VALUE);
      }

      if (this.scheduleM2Through4List != null && this.scheduleM2Through4List.length > 20) {
        this.scheduleM2Through4List[20].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through4List[0]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[2]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[3]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[4]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through4List[18]?.ATTRIB_VALUE);
      }

      if (this.scheduleM2Through8List != null && this.scheduleM2Through8List.length > 17) {
        this.scheduleM2Through8List[17].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through8List[5]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[8]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[9]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[10]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[11]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[12]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[13]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[14]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[15]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[16]?.ATTRIB_VALUE);

        this.scheduleM2Through8List[18].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through4List[5]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[8]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[9]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[10]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[13]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[14]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[15]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[16]?.ATTRIB_VALUE);
      }

      if (this.scheduleM2Through8List != null && this.scheduleM2Through8List.length > 19) {
        this.scheduleM2Through8List[19].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through8List[1]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[2]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2Through8List[17]?.ATTRIB_VALUE);
      }

      if (this.scheduleM2Through8List != null && this.scheduleM2Through8List.length > 20 && 
          this.scheduleM2Through4List != null && this.scheduleM2Through4List.length > 20) {
        this.scheduleM2Through8List[20].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2Through4List[20]?.ATTRIB_VALUE) - 
          this.toInt(this.scheduleM2Through8List[19]?.ATTRIB_VALUE);
      }

      if (this.scheduleM2OtherList != null && this.scheduleM2OtherList.length > 12) {
        this.scheduleM2OtherList[4].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2OtherList[2]?.ATTRIB_VALUE) -
          this.toInt(this.scheduleM2OtherList[3]?.ATTRIB_VALUE);
        this.scheduleM2OtherList[11].ATTRIB_VALUE = this.toInt(this.scheduleM2Through8List[20]?.ATTRIB_VALUE);
        this.scheduleM2OtherList[9].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2OtherList[8]?.ATTRIB_VALUE) +
          this.toInt(this.scheduleM2OtherList[7]?.ATTRIB_VALUE);
        this.scheduleM2OtherList[12].ATTRIB_VALUE = 
          this.toInt(this.scheduleM2OtherList[9]?.ATTRIB_VALUE) -
          this.toInt(this.scheduleM2OtherList[11]?.ATTRIB_VALUE);
      }
    }
    
    // Sync all calculated values back to master scheduleM2List to ensure consistency
    this.syncCalculatedValuesToMasterList();
    
    // Force change detection to update UI
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  /**
   * Update distributions with inline confirmation (similar to ng-really behavior)
   * Uses browser confirm() like other migrated components (f-4797, etc.)
   */
  updateDistributionsWithConfirm(row: any): void {
    // CRITICAL FIX: The user edits the Cash field directly in the table
    // scheduleM2Through8List[1].ATTRIB_VALUE = NEW value (what user just typed in DOM)
    // formListOriginal = OLD value (value before user edited it)
    
    // Get the CURRENT/OLD cash value (BEFORE user edited it) from formListOriginal
    let currentCashValue = 0;
    if (this.scheduleM2Through8List.length > 1 && 
        this.scheduleM2Through8List[1].ATTRIB_DESCRIPTION == 'Cash') {
      // Find the original Cash value from formListOriginal
      const cashItemOriginal = this.formListOriginal.find(item => 
        item.LINE_ATTRIB_KEY === this.scheduleM2Through8List[1].LINE_ATTRIB_KEY ||
        (item.ATTRIB_DESCRIPTION === 'Cash' && item.ATTRIB_SHORT_CODE === this.scheduleM2Through8List[1].ATTRIB_SHORT_CODE)
      );
      if (cashItemOriginal) {
        currentCashValue = this.toInt(cashItemOriginal.ATTRIB_VALUE);
      } else {
        // Fallback: use current value if original not found
        currentCashValue = this.toInt(this.scheduleM2Through8List[1].ATTRIB_VALUE);
      }
    }
    
    // NEW cash value is from scheduleM2Through8List[1].ATTRIB_VALUE - this is what user just typed in DOM
    // The user edits the Cash field directly, so this contains the NEW value
    const newCashValue = (this.scheduleM2Through8List.length > 1 && 
                          this.scheduleM2Through8List[1].ATTRIB_DESCRIPTION == 'Cash')
                         ? this.toInt(this.scheduleM2Through8List[1].ATTRIB_VALUE)
                         : 0;
    
    // Show confirmation only if current value is not 0 (matching original behavior)
    // Message: "from [current/old] to [new]" - current is BEFORE user edit, new is FROM DOM user input
    if (currentCashValue != 0) {
      const confirmMessage = `Are you sure to override Distributions cash amount from ${currentCashValue} to ${newCashValue}?`;
      if (!confirm(confirmMessage)) {
        return; // User cancelled
      }
    }
    
    // Apply the update
    this.updateDistributions(row);
  }

  updateDistributions(row?: any) {
    // CRITICAL FIX: The user edits the Cash field directly in the table via two-way binding
    // scheduleM2Through8List[1].ATTRIB_VALUE already contains the NEW value (user input)
    // We don't need to update it - it's already updated by the user's input!
    // We just need to sync it to master list and recalculate
    
    if (this.scheduleM2Through8List.length > 1 && 
        this.scheduleM2Through8List[1].ATTRIB_DESCRIPTION == 'Cash') {
      // The NEW cash value is already in scheduleM2Through8List[1].ATTRIB_VALUE (user edited it)
      // Ensure it's converted to a number
      const newCashValue = this.toInt(this.scheduleM2Through8List[1].ATTRIB_VALUE);
      
      // Sync to the master scheduleM2List to keep it in sync
      const cashItemInMasterList = this.scheduleM2List.find(item => 
        item.LINE_ATTRIB_KEY === this.scheduleM2Through8List[1].LINE_ATTRIB_KEY ||
        (item.ATTRIB_DESCRIPTION === 'Cash' && item.ATTRIB_SHORT_CODE === this.scheduleM2Through8List[1].ATTRIB_SHORT_CODE)
      );
      if (cashItemInMasterList) {
        cashItemInMasterList.ATTRIB_VALUE = newCashValue;
      }
      
      // Update formListOriginal to reflect the new value for future comparisons
      const cashItemOriginal = this.formListOriginal.find(item => 
        item.LINE_ATTRIB_KEY === this.scheduleM2Through8List[1].LINE_ATTRIB_KEY ||
        (item.ATTRIB_DESCRIPTION === 'Cash' && item.ATTRIB_SHORT_CODE === this.scheduleM2Through8List[1].ATTRIB_SHORT_CODE)
      );
      if (cashItemOriginal) {
        cashItemOriginal.ATTRIB_VALUE = newCashValue;
      }
    }
    
    // CRITICAL: Recalculate all totals and update other lines
    // The cash value is already updated in scheduleM2Through8List[1].ATTRIB_VALUE by user input
    // Match AngularJS: vm.updateOtherIncreases() - called without parameter
    this.updateOtherIncreases();
    
    // Force change detection after all calculations are complete
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  adjustGainLoss() {
    // Placeholder for adjustGainLoss logic if needed
    this.updateOtherIncreases(this.retainEarningsObj);
  }

  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      return value.toString();
    }
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  parseValue(value: string): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  }

  reset() {
    this.scheduleM2List = _.cloneDeep(this.formListOriginal);
    this.scheduleM2Through4List = [];
    this.scheduleM2Through8List = [];
    this.scheduleM2OtherList = [];
    this.scheduleM2HiddenList = [];

    if (this.scheduleM2Header?.FORM_NAME == 'F1120') {
      for (let i = 0; i < this.scheduleM2List.length; i++) {
        if (this.scheduleM2List[i].LINE_NO <= 4) {
          this.scheduleM2Through4List.push(this.scheduleM2List[i]);
        } else if (this.scheduleM2List[i].LINE_NO == 'N/A') {
          this.scheduleM2OtherList.push(this.scheduleM2List[i]);
        } else {
          if (this.scheduleM2List[i].ATTRIB_SHORT_CODE != 'CASH_AUTOCALC') {
            this.scheduleM2Through8List.push(this.scheduleM2List[i]);
          } else {
            this.scheduleM2HiddenList.push(this.scheduleM2List[i]);
          }
        }
      }
      if (this.scheduleM2OtherList.length > 6) {
        this.scheduleM2OtherList[6].ATTRIB_VALUE = 
          (this.scheduleM2OtherList[3]?.ATTRIB_VALUE || 0) - 
          (this.scheduleM2OtherList[5]?.ATTRIB_VALUE || 0);
      }
    } else {
      for (let i = 0; i < this.scheduleM2List.length; i++) {
        if (this.scheduleM2List[i].LINE_NO <= '5') {
          this.scheduleM2Through4List.push(this.scheduleM2List[i]);
        } else if (this.scheduleM2List[i].LINE_NO == 'N/A') {
          this.scheduleM2OtherList.push(this.scheduleM2List[i]);
        } else {
          if (this.scheduleM2List[i].ATTRIB_SHORT_CODE != 'CASH_AUTOCALC') {
            this.scheduleM2Through8List.push(this.scheduleM2List[i]);
          } else {
            this.scheduleM2HiddenList.push(this.scheduleM2List[i]);
          }
        }
      }
      if (this.scheduleM2OtherList.length > 12) {
        this.scheduleM2OtherList[12].ATTRIB_VALUE = 
          (this.scheduleM2OtherList[9]?.ATTRIB_VALUE || 0) - 
          (this.scheduleM2OtherList[11]?.ATTRIB_VALUE || 0);
      }
    }
    this.cdr.detectChanges();
  }

  save() {
    this.isSaveClicked = true;
    this.saving = true;
    this.crudLoading = true;

    const returnClobSettingsObj: any = {};
    const scheduleM2SaveDtls: any[] = [];
    
    // Filter to get only editable cells - matching AngularJS logic exactly
    const editableList = _.filter(this.scheduleM2List, (o: any) => {
      return o.IS_EDITABLE_CELL == 'Y';
    });

    // Deduplicate editable list to prevent saving duplicate records
    const seenSaveKeys = new Set<string>();
    const uniqueEditableList = editableList.filter((row: any) => {
      // Create unique key for save operation
      const saveKey = `${row.COMBINATION_KEY || ''}_${row.FORM_LINE_KEY || ''}_${row.LINE_ATTRIB_KEY || ''}_${row.ACCT_REF_KEY || ''}_${row.TRANS_DETAILS_KEY || ''}`;
      if (seenSaveKeys.has(saveKey)) {
        return false; // Duplicate, skip it
      }
      seenSaveKeys.add(saveKey);
      return true;
    });

    // Use proper array iteration instead of for...in to avoid issues
    for (let i = 0; i < uniqueEditableList.length; i++) {
      const row = uniqueEditableList[i];
      const returnObj: any = {};
      
      returnObj["object_id"] = this.rowData.object_id;
      returnObj["issue_key"] = row.ISSUE_KEY;
      returnObj['combination_key'] = row.COMBINATION_KEY;
      returnObj['form_key'] = row.FORM_KEY;
      returnObj['form_line_key'] = row.FORM_LINE_KEY;
      returnObj['line_attrib_key'] = row.LINE_ATTRIB_KEY;
      returnObj['trans_type_key'] = row.TRANS_TYPE_KEY;
      returnObj['attrib_type'] = row.ATTRIB_TYPE;
      returnObj['attrib_prop_key'] = row.ATTRIB_PROP_KEY;
      returnObj['acct_ref_key'] = row.ACCT_REF_KEY;
      returnObj['trans_details_key'] = row.TRANS_DETAILS_KEY;
      returnObj['attrib_value'] = row.ATTRIB_VALUE;
      returnObj['occurrence'] = (row.OCCURRENCE != null) ? parseInt(row.OCCURRENCE) : 1;
      
      scheduleM2SaveDtls.push(returnObj);
    }

    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['screen_key'] = this.globalService?.globalParams?.screen_key || '';
    returnClobSettingsObj['form_name'] = "F1120M-2";
    returnClobSettingsObj['me_code'] = this.rowData.ME_CODE;

    this.f1120SchM2Service.saveSchdM2(
      returnClobSettingsObj,
      scheduleM2SaveDtls,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.saving = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Defaults has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
          // Refetch data after successful save to ensure we have the latest data without duplicates
          this.fetchSchM2records();
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.saving = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}


